import { useEffect, useState } from 'react'
import { useGameStore, type Unit } from '../../store/gameStore'
import type { UnitType, UnitStatus, ResourceAmount, Mod, UnitAbility, Zone } from '@territory-nexus/shared'
import { XP_PER_LEVEL, LEVEL_CAPS, UNIT_COSTS, UNIT_META, UNIT_ABILITIES, RESOURCE_CONFIG, RESOURCE_ORDER, STATUS_CONFIG, canAffordCost, getTraitById, getZoneName as getZoneNameFromHex, MOD_META, RARITY_CONFIG } from '@territory-nexus/shared'
import {
  Loader2,
  Plus,
  MapPin,
  ArrowLeftCircle,
  Trash2,
  Shield,
  X,
  ChevronDown,
} from 'lucide-react'
import clsx from 'clsx'
import { UnitSprite } from './sprites'
import { api } from '../../services/api'

// Re-export for map visualization
export { UNIT_META as UNIT_INFO }

// ── Stat bar config for expanded view ──
const STAT_BAR = {
  hp:     { label: 'HP',  color: 'text-red-400',     bar: 'bg-red-400' },
  atk:    { label: 'ATK', color: 'text-orange-400',  bar: 'bg-orange-400' },
  def:    { label: 'DEF', color: 'text-blue-400',    bar: 'bg-blue-400' },
  spd:    { label: 'SPD', color: 'text-amber-400',   bar: 'bg-amber-400' },
  acc:    { label: 'ACC', color: 'text-emerald-400', bar: 'bg-emerald-400' },
  energy: { label: 'NRG', color: 'text-cyan-400',    bar: 'bg-cyan-400' },
} as const
const STAT_KEYS = Object.keys(STAT_BAR) as (keyof typeof STAT_BAR)[]
const STAT_MAX = 200

// ── Unit Card Component ──
function UnitCard({
  unit, info, ability, zoneName, trait, isMaxLevel,
  xpRequired, xpPercent, maxHp, currentHp, isDamaged, hpPercent,
  isExpanded, deployingUnit, selectedZone, playerZones,
  onToggleExpand, onRecall, onDeploy, onConfirmDeploy, onCancelDeploy, onSelectZone, onDisband,
}: {
  unit: Unit
  info: typeof UNIT_META[UnitType]
  ability: UnitAbility
  zoneName: string | null
  trait: ReturnType<typeof getTraitById> | null
  isMaxLevel: boolean
  xpRequired: number
  xpPercent: number
  maxHp: number
  currentHp: number
  isDamaged: boolean
  hpPercent: number
  isExpanded: boolean
  deployingUnit: string | null
  selectedZone: string | null
  playerZones: Zone[]
  onToggleExpand: () => void
  onRecall: () => void
  onDeploy: () => void
  onConfirmDeploy: () => void
  onCancelDeploy: () => void
  onSelectZone: (zoneId: string) => void
  onDisband: () => void
}) {
  // Fetch mods only when expanded
  const [mods, setMods] = useState<Mod[]>([])
  const [modsLoaded, setModsLoaded] = useState(false)
  useEffect(() => {
    if (isExpanded && !modsLoaded) {
      api.get<{ success: boolean; data: Mod[] }>(`/mods/unit/${unit.id}`)
        .then(res => { if (res.data.success) setMods(res.data.data) })
        .catch(() => {})
        .finally(() => setModsLoaded(true))
    }
  }, [isExpanded, modsLoaded, unit.id])

  return (
    <div className="rounded-xl border border-white/[0.06] overflow-hidden bg-white/[0.02]">
      {/* ── Collapsed card ── */}
      <div className="relative p-3">
        {/* Top-right action button */}
        <div className="absolute top-2.5 right-2.5 flex items-center gap-1">
          {unit.status === 'deployed' && (
            <button
              onClick={onRecall}
              className="px-2 py-1 rounded-md text-[11px] font-medium bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white transition flex items-center gap-1"
            >
              <ArrowLeftCircle className="w-3 h-3" />
              Recall
            </button>
          )}
          {unit.status === 'idle' && (
            <button
              onClick={onDeploy}
              className={clsx(
                'px-2 py-1 rounded-md text-[11px] font-medium transition flex items-center gap-1',
                deployingUnit === unit.id
                  ? 'bg-cyan-500 text-white'
                  : 'bg-cyan-500/15 text-cyan-400 hover:bg-cyan-500/25'
              )}
              disabled={playerZones.length === 0}
            >
              <MapPin className="w-3 h-3" />
              Deploy
            </button>
          )}
        </div>

        <div className="flex items-start gap-3">
          {/* Sprite */}
          <div className="shrink-0 mt-0.5">
            <UnitSprite
              unitType={unit.unitType}
              level={unit.level}
              status={unit.status}
              size="md"
              showLevel={false}
            />
          </div>

          {/* Info block */}
          <div className="flex-1 min-w-0 pr-20">
            {/* Name + level + trait */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={clsx('font-bold text-sm', info?.color)}>
                {info?.shortName || unit.unitType}
              </span>
              <span className="text-[10px] text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded font-mono">Lv.{unit.level}</span>
              {trait && (
                <span className={clsx(
                  'text-[10px] px-1.5 py-0.5 rounded font-semibold',
                  trait.color,
                  trait.color.includes('red') ? 'bg-red-500/15' :
                  trait.color.includes('blue') ? 'bg-blue-500/15' :
                  trait.color.includes('cyan') ? 'bg-cyan-500/15' :
                  trait.color.includes('violet') ? 'bg-violet-500/15' :
                  trait.color.includes('orange') ? 'bg-orange-500/15' :
                  trait.color.includes('amber') ? 'bg-amber-500/15' :
                  trait.color.includes('emerald') ? 'bg-emerald-500/15' :
                  trait.color.includes('slate') ? 'bg-slate-500/15' :
                  'bg-white/10'
                )}>
                  {trait.name}
                </span>
              )}
            </div>

            {/* Stats row */}
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[10px] font-mono text-red-400 bg-red-500/10 px-1.5 py-px rounded">{unit.stats.hp} <span className="text-red-400/60 text-[9px]">HP</span></span>
              <span className="text-[10px] font-mono text-orange-400 bg-orange-500/10 px-1.5 py-px rounded">{unit.stats.atk} <span className="text-orange-400/60 text-[9px]">ATK</span></span>
              <span className="text-[10px] font-mono text-blue-400 bg-blue-500/10 px-1.5 py-px rounded">{unit.stats.def} <span className="text-blue-400/60 text-[9px]">DEF</span></span>
              <span className="text-[10px] font-mono text-amber-400 bg-amber-500/10 px-1.5 py-px rounded">{unit.stats.spd} <span className="text-amber-400/60 text-[9px]">SPD</span></span>
            </div>

            {/* Zone location (deployed) */}
            {unit.status === 'deployed' && zoneName && (
              <div className="flex items-center gap-1 mt-1">
                <MapPin className="w-2.5 h-2.5 text-emerald-500/60" />
                <span className="text-[10px] text-emerald-400/70">{zoneName}</span>
              </div>
            )}

            {/* Progress bar: HP when damaged, XP otherwise */}
            <div className="flex items-center gap-1.5 mt-1.5">
              {isDamaged ? (
                <>
                  <div className="flex-1 h-1 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={clsx('h-full rounded-full', hpPercent > 50 ? 'bg-emerald-500' : hpPercent > 25 ? 'bg-amber-500' : 'bg-red-500')}
                      style={{ width: `${hpPercent}%` }}
                    />
                  </div>
                  <span className="text-[9px] font-mono text-slate-500">{currentHp}/{maxHp}</span>
                </>
              ) : (
                <>
                  <div className="flex-1 h-1 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={clsx('h-full rounded-full', isMaxLevel ? 'bg-amber-500' : 'bg-cyan-500/70')}
                      style={{ width: `${xpPercent}%` }}
                    />
                  </div>
                  <span className="text-[9px] font-mono text-slate-500">
                    {isMaxLevel ? 'MAX' : `${unit.xp}/${xpRequired}`}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Expand toggle — bottom center */}
        <button
          onClick={onToggleExpand}
          className="w-full flex items-center justify-center pt-1.5 -mb-1 text-slate-600 hover:text-slate-400 transition"
        >
          <ChevronDown className={clsx('w-4 h-4 transition-transform', isExpanded && 'rotate-180')} />
        </button>
      </div>

      {/* ── Deploy zone selector ── */}
      {deployingUnit === unit.id && (
        <div className="px-3 pb-3 border-t border-white/5">
          <div className="flex items-center gap-2 mt-2">
            <select
              value={selectedZone || ''}
              onChange={(e) => onSelectZone(e.target.value)}
              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-xs"
            >
              <option value="">Select zone...</option>
              {playerZones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {getZoneNameFromHex(zone.hexId)}
                </option>
              ))}
            </select>
            <button
              onClick={onConfirmDeploy}
              disabled={!selectedZone}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm
            </button>
            <button onClick={onCancelDeploy} className="p-1.5 text-slate-400 hover:text-white rounded-lg transition">
              <X className="w-4 h-4" />
            </button>
          </div>
          {playerZones.length === 0 && (
            <p className="text-xs text-amber-400 mt-2">No owned zones. Claim a zone first.</p>
          )}
        </div>
      )}

      {/* ── Expanded detail (complementary info only) ── */}
      {isExpanded && (
        <div className="border-t border-white/5 px-3 py-3 space-y-3">
          {/* Full stat bars */}
          <div>
            <div className="text-[9px] text-slate-600 uppercase tracking-wider mb-1.5">Stats</div>
            <div className="space-y-1">
              {STAT_KEYS.map(key => {
                const cfg = STAT_BAR[key]
                const val = unit.stats[key]
                const pct = Math.min(100, (val / STAT_MAX) * 100)
                return (
                  <div key={key} className="flex items-center gap-2">
                    <span className={clsx('text-[9px] font-mono w-6 text-right', cfg.color)}>{cfg.label}</span>
                    <div className="flex-1 h-1.5 bg-slate-800/80 rounded-full overflow-hidden">
                      <div className={clsx('h-full rounded-full', cfg.bar)} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[10px] font-mono text-slate-400 w-7 text-right">{val}</span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* HP (when damaged) */}
          {isDamaged && (
            <div>
              <div className="flex items-center justify-between text-[9px] mb-1">
                <span className="text-slate-600 uppercase tracking-wider">Health</span>
                <span className={clsx('font-mono', hpPercent > 50 ? 'text-emerald-400' : hpPercent > 25 ? 'text-amber-400' : 'text-red-400')}>
                  {currentHp}/{maxHp}
                </span>
              </div>
              <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className={clsx('h-full rounded-full', hpPercent > 50 ? 'bg-emerald-400' : hpPercent > 25 ? 'bg-amber-400' : 'bg-red-400')}
                  style={{ width: `${hpPercent}%` }}
                />
              </div>
            </div>
          )}

          {/* XP */}
          <div>
            <div className="flex items-center justify-between text-[9px] mb-1">
              <span className="text-slate-600 uppercase tracking-wider">Experience</span>
              <span className="text-slate-400 font-mono">
                {isMaxLevel ? 'MAX' : `${unit.xp}/${xpRequired} XP`}
              </span>
            </div>
            <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div
                className={clsx('h-full rounded-full', isMaxLevel ? 'bg-amber-400' : 'bg-cyan-500')}
                style={{ width: `${xpPercent}%` }}
              />
            </div>
          </div>

          {/* Passive ability */}
          {ability && (
            <div className="flex items-start gap-2 px-2 py-1.5 rounded-lg bg-purple-500/[0.06] border border-purple-500/10">
              <span className="text-purple-400 text-[10px] mt-px">&#10022;</span>
              <div>
                <span className="text-[10px] font-semibold text-purple-300">{ability.name}</span>
                <span className="text-[10px] text-purple-400/70 ml-1.5">{ability.description}</span>
              </div>
            </div>
          )}

          {/* Equipped mods */}
          {mods.length > 0 && (
            <div>
              <div className="text-[9px] text-slate-600 uppercase tracking-wider mb-1.5">Equipped Mods</div>
              <div className="flex gap-1.5 flex-wrap">
                {mods.map(mod => {
                  const meta = MOD_META[mod.modType]
                  const rarity = RARITY_CONFIG[mod.rarity]
                  return (
                    <div
                      key={mod.id}
                      className={clsx('flex items-center gap-1 px-2 py-1 rounded-md text-xs border', rarity.bg, rarity.border)}
                      title={Object.entries(mod.statBonuses).filter(([, v]) => v && v > 0).map(([k, v]) => `+${v} ${k.toUpperCase()}`).join(', ')}
                    >
                      <span>{meta.icon}</span>
                      <span className={rarity.color}>{meta.shortName}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Disband action */}
          {unit.status !== 'in_battle' && (
            <div className="pt-1 border-t border-white/5">
              <button
                onClick={onDisband}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-red-400/50 hover:text-red-400 hover:bg-red-500/10 transition"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Disband
                <span className="text-red-400/30 ml-1">
                  (+{RESOURCE_ORDER.filter(k => UNIT_COSTS[unit.unitType][k] > 0).map(k =>
                    `${RESOURCE_CONFIG[k].symbol}${Math.floor(UNIT_COSTS[unit.unitType][k] * 0.5)}`
                  ).join(' ')})
                </span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function UnitsPanel() {
  const { units, isLoadingUnits, playerZones, resources, fetchUnits, createUnit, recallUnit, disbandUnit, deployUnit } =
    useGameStore()
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [deployingUnit, setDeployingUnit] = useState<string | null>(null)
  const [selectedZone, setSelectedZone] = useState<string | null>(null)
  const [selectedUnit, setSelectedUnit] = useState<string | null>(null)

  const getZoneName = (zoneId: string | null) => {
    if (!zoneId) return null
    const zone = playerZones.find(z => z.id === zoneId)
    return zone ? getZoneNameFromHex(zone.hexId) : null
  }

  useEffect(() => {
    fetchUnits()
  }, [fetchUnits])

  const handleCreate = async (unitType: UnitType) => {
    setCreating(true)
    setError(null)
    try {
      await createUnit(unitType)
      setShowCreate(false)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to create unit')
    } finally {
      setCreating(false)
    }
  }

  const handleRecall = async (unitId: string) => {
    try {
      await recallUnit(unitId)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to recall unit')
    }
  }

  const handleDisband = async (unitId: string, unitType: UnitType) => {
    const cost = UNIT_COSTS[unitType]
    const refundText = RESOURCE_ORDER
      .filter(k => cost[k] > 0)
      .map(k => `${RESOURCE_CONFIG[k].symbol}${Math.floor(cost[k] * 0.5)}`)
      .join(' ')
    if (!confirm(`Disband this unit?\n\n50% refund: ${refundText}`)) return
    try {
      await disbandUnit(unitId)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to disband unit')
    }
  }

  const handleDeploy = async (unitId: string) => {
    if (!selectedZone) return
    try {
      await deployUnit(unitId, selectedZone)
      setDeployingUnit(null)
      setSelectedZone(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to deploy unit')
    }
  }

  const canAfford = (cost: ResourceAmount) => {
    return canAffordCost(resources as ResourceAmount, cost)
  }

  if (isLoadingUnits) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-display font-bold text-white">Units</h2>
            <p className="text-slate-500 text-xs mt-0.5">
              {units.length} total &middot; {units.filter((u) => u.status === 'deployed').length} deployed
            </p>
          </div>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition',
              showCreate
                ? 'bg-slate-700 text-slate-300'
                : 'bg-cyan-500/15 text-cyan-400 hover:bg-cyan-500/25 border border-cyan-500/30'
            )}
          >
            {showCreate ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showCreate ? 'Cancel' : 'Create'}
          </button>
        </div>

        {error && (
          <div className="glass border border-red-500/30 bg-red-500/10 rounded-lg p-3 mb-4">
            <p className="text-red-400 text-sm">{error}</p>
            <button onClick={() => setError(null)} className="text-xs text-red-400/60 hover:text-red-400 mt-1">
              Dismiss
            </button>
          </div>
        )}

        {/* Create Unit Panel */}
        {showCreate && (
          <div className="glass rounded-xl p-4 mb-4 border border-cyan-500/20">
            <h3 className="text-sm font-semibold text-slate-400 mb-3">Create New Unit</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {(Object.keys(UNIT_META) as UnitType[]).map((type) => {
                const info = UNIT_META[type]
                const cost = UNIT_COSTS[type]
                const affordable = canAfford(cost)

                return (
                  <button
                    key={type}
                    onClick={() => handleCreate(type)}
                    disabled={creating || !affordable}
                    className={clsx(
                      'rounded-lg p-3 text-center transition-all border',
                      affordable
                        ? 'bg-white/5 border-white/10 hover:border-cyan-500/40 hover:bg-white/10'
                        : 'bg-slate-900/60 border-white/5 cursor-not-allowed'
                    )}
                  >
                    <div className={clsx('flex justify-center mb-2', !affordable && 'grayscale opacity-40')}>
                      <UnitSprite
                        unitType={type}
                        level={1}
                        status="idle"
                        size="lg"
                        showLevel={false}
                        animate={false}
                      />
                    </div>
                    <h4 className={clsx('font-semibold text-xs', affordable ? info.color : 'text-slate-500')}>{info.name}</h4>
                    <div className="flex items-center justify-center gap-2 mt-1.5 text-xs flex-wrap">
                      {RESOURCE_ORDER.map((resKey) => {
                        if (cost[resKey] <= 0) return null
                        const rc = RESOURCE_CONFIG[resKey]
                        const playerAmount = resKey === 'energy' ? resources.energy
                          : resKey === 'nanomaterials' ? resources.nanomaterials
                          : resKey === 'quantum' ? resources.quantum
                          : resources.darkMatter
                        const hasEnough = playerAmount >= cost[resKey]
                        return (
                          <span key={resKey} className={clsx('flex items-center gap-0.5 font-medium', hasEnough ? rc.color : 'text-red-400')}>
                            {rc.symbol}{cost[resKey]}
                          </span>
                        )
                      })}
                    </div>
                  </button>
                )
              })}
            </div>
            {creating && (
              <div className="flex items-center justify-center gap-2 mt-3 text-cyan-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Creating...</span>
              </div>
            )}
          </div>
        )}

        {/* Units List */}
        {units.length === 0 ? (
          <div className="glass rounded-xl p-8 text-center border border-white/5">
            <Shield className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-slate-300 mb-2">No Units</h3>
            <p className="text-slate-400 text-sm mb-4">Create your first unit to start building your army</p>
            <button onClick={() => setShowCreate(true)} className="btn-primary text-sm">
              <Plus className="w-4 h-4 mr-1" />
              Create Unit
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {(['deployed', 'idle', 'in_transit', 'in_battle'] as UnitStatus[]).map(status => {
              const statusUnits = units.filter(u => u.status === status)
              if (statusUnits.length === 0) return null

              return (
                <div key={status}>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                    <span className={clsx('w-1.5 h-1.5 rounded-full', STATUS_CONFIG[status]?.dotColor)} />
                    {STATUS_CONFIG[status]?.label} ({statusUnits.length})
                  </div>
                  <div className="space-y-2">
                    {statusUnits.map((unit) => {
                      const info = UNIT_META[unit.unitType]
                      const ability = UNIT_ABILITIES[unit.unitType]
                      const zoneName = getZoneName(unit.zoneId)
                      const isMaxLevel = unit.level >= LEVEL_CAPS.unit
                      const xpRequired = isMaxLevel ? 0 : XP_PER_LEVEL(unit.level)
                      const xpPercent = isMaxLevel ? 100 : Math.min(100, (unit.xp / xpRequired) * 100)
                      const trait = unit.trait ? getTraitById(unit.trait) : null
                      const maxHp = unit.stats.hp
                      const currentHp = unit.currentHp ?? maxHp
                      const isDamaged = currentHp < maxHp
                      const hpPercent = Math.min(100, (currentHp / maxHp) * 100)
                      const isExpanded = selectedUnit === unit.id

                      return (
                        <UnitCard
                          key={unit.id}
                          unit={unit}
                          info={info}
                          ability={ability}
                          zoneName={zoneName}
                          trait={trait}
                          isMaxLevel={isMaxLevel}
                          xpRequired={xpRequired}
                          xpPercent={xpPercent}
                          maxHp={maxHp}
                          currentHp={currentHp}
                          isDamaged={isDamaged}
                          hpPercent={hpPercent}
                          isExpanded={isExpanded}
                          deployingUnit={deployingUnit}
                          selectedZone={selectedZone}
                          playerZones={playerZones}
                          onToggleExpand={() => setSelectedUnit(isExpanded ? null : unit.id)}
                          onRecall={() => handleRecall(unit.id)}
                          onDeploy={() => setDeployingUnit(deployingUnit === unit.id ? null : unit.id)}
                          onConfirmDeploy={() => handleDeploy(unit.id)}
                          onCancelDeploy={() => { setDeployingUnit(null); setSelectedZone(null) }}
                          onSelectZone={setSelectedZone}
                          onDisband={() => handleDisband(unit.id, unit.unitType)}
                        />
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
